import React, { useEffect, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import {
  Button,
  Label,
  Input,
  FieldGroup,
  Text,
  Link,
  PasswordInput,
  Header,
  Checkbox,
  Base,
  Fields,
} from '@pet-notify/ui'

import tagImage from './images/tag.png'
import ErrorMessage from 'components/ErrorMessage'
import { isEmailAvailable } from 'services/users'
import type { AccountInput } from 'services/orders'
import { useFetchPromoCode } from 'hooks/promo-codes'
import style from './style.module.css'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup
    .string()
    .email('Email must be a valid email address')
    .required('Email is required')
    .test('is-unique', 'Email is already in use', async (email) => {
      if (email) {
        try {
          const response = await isEmailAvailable(email)
          return response
        } catch (error) {
          return false
        }
      }
      return false
    }),
  confirmEmail: yup
    .string()
    .required('Confirm Email is required')
    .oneOf([yup.ref('email'), null], 'Must match email'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must contain 6 characters'),
  confirmPassword: yup
    .string()
    .required('Confirm Password is required')
    .oneOf([yup.ref('password'), null], 'Must match password'),
  phone: yup.string().required('Phone is required'),
  agree: yup
    .boolean()
    .oneOf(
      [true],
      'Please verify you understand that Pet Notify is only available in the US and Canada',
    ),
})

type FormData = AccountInput & {
  agree: boolean
  confirmEmail: string
}

type Props = {
  state: Partial<AccountInput>
  onComplete: (data: AccountInput) => void
}

function AccountPage({ state, onComplete }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: state,
  })

  const [captchaRequired, setCaptchaRequired] = useState(!!RECAPTCHA_SITE_KEY)
  const fetchPromoCodeMutation = useFetchPromoCode()

  useEffect(() => {
    reset(state)
  }, [state])

  const onSubmit = handleSubmit((data: FormData) => {
    const {
      promoCode,
      firstName,
      lastName,
      email,
      password,
      confirmPassword,
      phone,
    } = data

    fetchPromoCodeMutation.mutate(
      { promoCode },
      {
        onSuccess: () => {
          onComplete({
            promoCode,
            firstName,
            lastName,
            email,
            password,
            confirmPassword,
            phone,
          })
        },
      },
    )
  })

  return (
    <form onSubmit={onSubmit}>
      <div className={style.image}>
        <img src={tagImage} alt='Sample Pet Notify Intellitag with packaging' />
      </div>
      <Header as='h1' size='xlarge' mb='6'>
        Intellitag Account Activation
      </Header>
      <Text mb='6'>
        Welcome to Pet Notify! Enter the information below to customize your
        pet&apos;s Intellitag. One household account powers unlimited pet
        Intellitags.
      </Text>
      <Text mb='6'>
        There are no commitments, and you may cancel anytime directly through
        the Pet Notify app, which you can download through the{' '}
        <Link
          href={import.meta.env.VITE_IOS_APP_STORE_LINK}
          target='_blank'
          rel='noopener noreferrer'
        >
          App Store
        </Link>{' '}
        or{' '}
        <Link
          href={import.meta.env.VITE_GOOGLE_PLAY_STORE_LINK}
          target='_blank'
          rel='noopener noreferrer'
        >
          Google Play
        </Link>{' '}
        after your account is created.
      </Text>

      <Fields>
        <FieldGroup>
          <Label htmlFor='firstName'>First Name</Label>
          <Input
            type='text'
            id='firstName'
            {...register('firstName', { required: true })}
          />
        </FieldGroup>
        <FieldGroup>
          <Label htmlFor='lastName'>Last Name</Label>
          <Input
            type='text'
            id='lastName'
            {...register('lastName', { required: true })}
          />
        </FieldGroup>
        <FieldGroup>
          <Label htmlFor='phone' mb='0'>
            Phone
          </Label>
          <Input id='phone' {...register('phone', { required: true })} />
        </FieldGroup>
        {errors.phone && <ErrorMessage error={errors.phone.message} />}

        <FieldGroup>
          <Label htmlFor='email'>Email</Label>
          <Input type='email' id='email' {...register('email')} />
        </FieldGroup>
        {errors.email && <ErrorMessage error={errors.email.message} />}
        <FieldGroup>
          <Label htmlFor='confirmEmail'>Confirm Email</Label>
          <Input
            type='email'
            id='confirmEmail'
            {...register('confirmEmail', { required: true })}
          />
        </FieldGroup>
        {errors.confirmEmail && (
          <ErrorMessage error={errors.confirmEmail.message} />
        )}

        <FieldGroup>
          <Label htmlFor='promoCode'>Payment Confirmation Code</Label>
          <Input
            type='text'
            id='promoCode'
            required
            {...register('promoCode')}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor='password' mb='0'>
            Create Password
          </Label>

          <Text variant='note' mb='2'>
            Must contain a minimum of 6 characters including a number
          </Text>
          <PasswordInput
            id='password'
            {...register('password', { required: true })}
          />
        </FieldGroup>
        {errors.password && <ErrorMessage error={errors.password.message} />}
        <FieldGroup>
          <Label htmlFor='confirmPassword'>Confirm Password</Label>
          <PasswordInput
            id='confirmPassword'
            {...register('confirmPassword', { required: true })}
          />
        </FieldGroup>

        {errors.confirmPassword && (
          <ErrorMessage error={errors.confirmPassword.message} />
        )}

        {fetchPromoCodeMutation.isError && (
          <ErrorMessage error={fetchPromoCodeMutation.error} />
        )}

        {/* <FieldGroup>
          <Checkbox
            label='I understand Pet Notify is only available in the US and Canada'
            id='signup-agree'
            {...register('agree')}
          />
        </FieldGroup>

        {errors.agree && <ErrorMessage error={errors.agree.message} />} */}

        {RECAPTCHA_SITE_KEY && (
          <Base mb='6'>
            <ReCAPTCHA
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={() => {
                setCaptchaRequired(false)
              }}
            />
          </Base>
        )}
      </Fields>

      <Button mb='8' type='submit' disabled={captchaRequired}>
        Continue
      </Button>

      <Text>
        Having trouble?{' '}
        <Link href='mailto:support@petnotify.com'>Email us</Link> and we&apos;ll
        help you sort it out.
      </Text>
    </form>
  )
}

export default AccountPage
